import { getSentimentLevel } from '@polaris/core/constants'
import { Gauge } from 'lucide-react'
import Image from 'next/image'

interface SentimentBadgeProps {
  level?: number
  isLoading?: boolean
}

export function SentimentBadge({
  level = 50,
  isLoading = false,
}: SentimentBadgeProps) {
  const sentiment = getSentimentLevel(level)

  // We are sharing sentiment config with mobile where we store the design token
  // to be used when displaying the badge. To use tailwind classes for this
  // we would need to redefine every option here to ensure that the tw class
  // can be found in the code and won't be stripped. That's a very brittle
  // approach and we should avoid it. Instead we build on the css variable being
  // defined according to the design token. That's a safe bet.
  const backgroundStyle = {
    backgroundColor: `rgb(var(--${sentiment.bgColor}))`,
  }
  const textStyle = {
    color: `rgb(var(--${sentiment.textColor}))`,
  }

  if (isLoading) {
    return (
      <div className='animate-pulse rounded-2xl min-h-16 min-w-[200px]'>
        <div className='bg-surface2 h-full w-full rounded-2xl' />
      </div>
    )
  }

  return (
    <div
      style={backgroundStyle}
      className='flex flex-row items-center justify-start overflow-clip rounded-2xl min-w-64 min-h-16'
    >
      <div className='flex flex-col items-start justify-center px-3 py-2'>
        <div className='flex gap-1.5 items-center justify-start'>
          <Gauge size={16} style={textStyle} />
          <span
            style={textStyle}
            className='block  font-normal text-nowrap text-sm'
          >
            Sentiment {level ?? 'N/A'}/100
          </span>
        </div>
        <div className='font-semibold leading-5 text-sm' style={textStyle}>
          {sentiment.description}
        </div>
      </div>

      <div className='relative left-2 w-[88px] h-16 shrink-0'>
        <Image
          src={sentiment.imageUrl}
          alt={sentiment.label}
          fill
          className='object-cover'
          sizes='88px'
        />
      </div>
    </div>
  )
}
